import { loadRailgunSdk } from '@web/utils/railgunSdk'
import { railgunEmitter } from '@web/utils/railgunListener'
import {
  NETWORK_CONFIG,
  type RailgunBalancesEvent,
  type NetworkName,
  type TransactionHistoryItem
} from '@railgun-community/shared-models'
import { Buffer } from 'buffer'

import {
  MAX_SANE_POI_NOTE_AMOUNT,
  type RailgunTokenBalance,
  type RailgunTokenBalanceMap
} from '@web/utils/railgunTokens'
import {
  deriveEffectiveRailgunMnemonic,
  deriveEncryptionKey,
  normalizeMnemonic
} from '@web/utils/railgunMnemonic'
import { RAILGUN_DEBUG } from '@web/utils/railgunCore'
import { refreshBalancesMaybeWorker } from '@web/extension-services/offscreen/scanWorker/railgunScanWorkerClient'
import { syncProfiler } from '@web/utils/railgunSyncProfiler'

export type RailgunWalletResult = {
  id: string
  railgunAddress: string
  encryptionKey: string
}

export type RailgunBalanceResult = {
  balance: RailgunTokenBalanceMap
  pendingBalance: RailgunTokenBalanceMap
  missingInternalPOIBalance: RailgunTokenBalanceMap
  missingExternalPOIBalance: RailgunTokenBalanceMap
}

/**
 * Drop missing-POI notes whose raw amount exceeds {@link MAX_SANE_POI_NOTE_AMOUNT} (corrupt) so
 * they never get persisted into the SW snapshot. See that constant for the full rationale.
 */
function filterCorruptPoiNotes(amounts: RailgunTokenBalance[]): RailgunTokenBalance[] {
  return amounts.filter((a) => a.amount <= MAX_SANE_POI_NOTE_AMOUNT)
}

/** Subset of wallet row used to compare persisted identity (not balances or other UI state). */
export type RailgunWalletPersistedIdentity = {
  accountAddr: string
  walletId?: string
  encryptionKey?: string
}

/**
 * Whether two wallet lists match as persisted identities: same accounts (case-insensitive address),
 * same length, and same `walletId` / `encryptionKey` for each paired row after sorting by address.
 * Use to avoid replacing React state when storage reload would drop in-memory-only fields.
 */
export function persistedRailgunWalletsEqual(
  a: ReadonlyArray<RailgunWalletPersistedIdentity>,
  b: ReadonlyArray<RailgunWalletPersistedIdentity>
): boolean {
  if (a.length !== b.length) return false
  const sortKey = (w: RailgunWalletPersistedIdentity) => w.accountAddr.toLowerCase()
  const A = [...a].sort((x, y) => sortKey(x).localeCompare(sortKey(y)))
  const B = [...b].sort((x, y) => sortKey(x).localeCompare(sortKey(y)))
  for (let i = 0; i < A.length; i++) {
    const wa = A[i]!
    const wb = B[i]!
    if (sortKey(wa) !== sortKey(wb)) return false
    if (wa.walletId !== wb.walletId) return false
    if (wa.encryptionKey !== wb.encryptionKey) return false
  }
  return true
}

const BALANCE_WAIT_TIMEOUT_MS = 30 * 60 * 1000 // 20 minutes

/**
 * RailgunWallet class that wraps the official Railgun wallet and provides
 * convenient methods. Stops at create account - balance/shield are stubs.
 */
export class RailgunWallet {
  private walletId: string
  private railgunAddress: string
  private encryptionKey: string

  constructor(data: RailgunWalletResult) {
    this.walletId = data.id
    this.railgunAddress = data.railgunAddress
    this.encryptionKey = data.encryptionKey
  }

  getWalletId(): string {
    return this.walletId
  }

  getEncryptionKey(): string {
    return this.encryptionKey
  }

  /**
   * Get the Railgun address (0zk...)
   */
  async getRailgunAddress(): Promise<string> {
    return this.railgunAddress
  }

  /**
   * Get the viewing key pair (for internal use).
   * Returns shareable viewing key as hex - format differs from Kohaku but compatible for storage.
   */
  async getViewingKeyPair(): Promise<{ privateKey: Uint8Array; publicKey: Uint8Array }> {
    const { getWalletShareableViewingKey } = await loadRailgunSdk()
    const shareableKey = await getWalletShareableViewingKey(this.walletId)
    // Context expects { privateKey, publicKey }; encode shareable key for storage compatibility
    const keyBytes = Buffer.from(shareableKey, 'utf8')
    return { privateKey: new Uint8Array(keyBytes), publicKey: new Uint8Array(32) }
  }

  /**
   * Get the shareable viewing key string (official SDK format).
   */
  async getShareableViewingKey(): Promise<string> {
    const { getWalletShareableViewingKey } = await loadRailgunSdk()
    return getWalletShareableViewingKey(this.walletId)
  }

  /**
   * Fetch private balances for the given networks. Loads providers if needed, triggers
   * refreshBalances, and returns Spendable balance plus ShieldPending (pending shield) per chain.
   */
  async getBalance(networks: NetworkName[]): Promise<RailgunBalanceResult> {
    const { refreshBalances } = await loadRailgunSdk()
    const chainsToScan: Array<{ name: NetworkName; chainId: number }> = networks.map((name) => ({
      name,
      chainId: NETWORK_CONFIG[name].chain.id
    }))
    const chainTokenBalances: Map<number, RailgunTokenBalance[]> = new Map()
    const chainPendingBalances: Map<number, RailgunTokenBalance[]> = new Map()
    const chainMissingInternalPOIBalances: Map<number, RailgunTokenBalance[]> = new Map()
    const chainMissingExternalPOIBalances: Map<number, RailgunTokenBalance[]> = new Map()
    const walletId = this.walletId

    if (RAILGUN_DEBUG) {
      console.log('[Railgun getBalance] started', { walletId: walletId.slice(0, 8) + '…' })
    }

    // Phase profiler — TEMPORARY first-sync breakdown measurement. New window per call.
    syncProfiler.beginSession()

    return new Promise<RailgunBalanceResult>((resolve) => {
      let settled = false
      const snapshot = (): RailgunBalanceResult => ({
        balance: new Map(chainTokenBalances),
        pendingBalance: new Map(chainPendingBalances),
        missingInternalPOIBalance: new Map(chainMissingInternalPOIBalances),
        missingExternalPOIBalance: new Map(chainMissingExternalPOIBalances)
      })
      // Subscribe via railgunEmitter (the persistent broadcaster set at engine init) instead of
      // setOnBalanceUpdateCallback. The latter is a singleton in the Railgun SDK — calling it
      // here would clobber the init-time broadcaster, and unsetting on finish would permanently
      // silence the event stream that railgunContext / SW listeners depend on. Subscribing
      // through the emitter lets multiple concurrent getBalance() calls coexist without
      // trampling each other's listeners.
      const onEvent = (event: RailgunBalancesEvent) => {
        if (RAILGUN_DEBUG) {
          console.log('[Railgun getBalance] event', {
            chainId: event.chain.id,
            balanceBucket: event.balanceBucket,
            amounts: event.erc20Amounts
          })
        }
        if (event.railgunWalletID !== walletId) return
        const network = chainsToScan.find((c) => c.chainId === event.chain.id)
        if (!network) return
        syncProfiler.balanceBucket(event.chain.id, event.balanceBucket)
        switch (event.balanceBucket) {
          case 'Spendable':
            chainTokenBalances.set(event.chain.id, event.erc20Amounts)
            break
          case 'ShieldPending':
            chainPendingBalances.set(event.chain.id, event.erc20Amounts)
            break
          case 'MissingInternalPOI':
            chainMissingInternalPOIBalances.set(
              event.chain.id,
              filterCorruptPoiNotes(event.erc20Amounts)
            )
            break
          case 'MissingExternalPOI':
            chainMissingExternalPOIBalances.set(
              event.chain.id,
              filterCorruptPoiNotes(event.erc20Amounts)
            )
            break
          default:
            break
        }
      }

      const finish = (result: RailgunBalanceResult) => {
        if (settled) return
        settled = true
        clearTimeout(timeout)
        railgunEmitter.off('balanceUpdate', onEvent)
        // Phase 5.1 debug — always-on per-chain bucket summary (RAILGUN_DEBUG is off in prod, so
        // the per-event logs above are silent). Shows exactly which bucket each chain's notes
        // landed in: empty Spendable + non-empty MissingInternal/External = "pending POI"; all
        // empty after a Complete scan = the engine-empty-result race we're chasing.
        const sizes = (m: RailgunTokenBalanceMap) =>
          Object.fromEntries([...m.entries()].map(([cid, arr]) => [cid, arr.length]))
        // eslint-disable-next-line no-console
        console.log('[Railgun getBalance] resolved buckets', {
          networks,
          spendable: sizes(result.balance),
          shieldPending: sizes(result.pendingBalance),
          missingInternalPOI: sizes(result.missingInternalPOIBalance),
          missingExternalPOI: sizes(result.missingExternalPOIBalance)
        })
        resolve(result)
      }

      const timeout = setTimeout(() => {
        if (RAILGUN_DEBUG) {
          console.log('[Railgun getBalance] timeout fired')
        }
        finish(snapshot())
      }, BALANCE_WAIT_TIMEOUT_MS)

      railgunEmitter.on('balanceUpdate', onEvent)

      // Resolve after refreshBalances has settled for every chain rather than on the
      // first Spendable event. The Railgun SDK emits one event per (chain, bucket) and
      // the Spendable event for a chain typically arrives BEFORE that chain's
      // MissingInternalPOI / MissingExternalPOI events. Resolving on Spendable left
      // the pending-POI maps empty whenever a fresh send had just produced a change
      // UTXO awaiting POI, so the dashboard total dropped to whatever was still
      // spendable (e.g. $2.70 → $0.96 right after a 0.1 USDC send) with no
      // "+pending POI" subscript to explain the gap. Waiting for refreshBalances to
      // resolve guarantees every bucket event has been delivered to the callback.
      const run = async () => {
        const settledResults = await Promise.allSettled(
          chainsToScan.map(async ({ name, chainId }) => {
            const chain = NETWORK_CONFIG[name].chain

            if (RAILGUN_DEBUG) {
              console.log('[Railgun getBalance] refreshBalances', { chainId: chain.id, name })
            }

            syncProfiler.refreshStart(chain.id, name)
            try {
              // Phase 2: route worker-enabled chains (BSC today) to a view-only
              // scan worker so their decrypt CPU runs off this thread. Returns
              // false for non-routed chains; throws if the worker path failed,
              // in which case we fall back to a main-thread scan so a balance is
              // never lost — only the parallelism. See railgun-parallel-scan.md §4.
              let handledByWorker = false
              try {
                handledByWorker = await refreshBalancesMaybeWorker(chain, walletId, {
                  encryptionKey: this.encryptionKey,
                  getShareableViewingKey: () => this.getShareableViewingKey()
                })
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[Railgun getBalance] scan worker failed; main-thread fallback', {
                  chainId: chain.id,
                  error: err instanceof Error ? err.message : String(err)
                })
                handledByWorker = false
              }
              if (!handledByWorker) {
                await refreshBalances(chain, [walletId])
              }
            } finally {
              syncProfiler.refreshEnd(chain.id)
            }
          })
        )

        if (RAILGUN_DEBUG) {
          console.log('[Railgun getBalance] refreshBalances settled', {
            successCount: settledResults.filter((r) => r.status === 'fulfilled').length,
            failedCount: settledResults.filter((r) => r.status === 'rejected').length
          })
        }

        // Defer one tick so any callback the SDK invokes synchronously after its
        // refreshBalances promise resolves still lands in our maps.
        setTimeout(() => {
          syncProfiler.printSummary(chainsToScan.map((c) => c.chainId))
          finish(snapshot())
        }, 0)
      }
      void run()
    })
  }

  /**
   * Get private transaction history for the given network.
   * Only works after the engine has been initialized and synced (refreshBalances called at least once).
   * @param networkName - The network to query (e.g. NetworkName.Ethereum)
   * @param startingBlock - Optional block number to start from; omit for full history
   */
  async getTransactionHistory(
    networkName: NetworkName,
    startingBlock?: number
  ): Promise<TransactionHistoryItem[]> {
    const { walletForID, getWalletTransactionHistory } = await loadRailgunSdk()
    const chain = NETWORK_CONFIG[networkName].chain

    if (RAILGUN_DEBUG) {
      console.log('[Railgun getTransactionHistory]', {
        networkName,
        chainId: chain.id,
        walletId: this.walletId.slice(0, 8) + '…'
      })
    }

    // The per-chain scan worker writes receive/sent commitments into this wallet's
    // DB from a separate JS context, so the page-side wallet object's in-RAM
    // `receiveCommitmentsCache` goes stale and `getWalletTransactionHistory`
    // (which reads it via `wallet.TXOs()`) keeps returning the pre-scan list —
    // new Shields never appear in Activity until a full page reload. Drop the
    // cache for this chain first so `TXOs()` re-queries the DB. Mirrors the
    // offscreen-path fix in `sdkRegistry.ts` `getWalletTransactionHistory`.
    try {
      const wallet = walletForID(this.walletId) as unknown as {
        invalidateCommitmentsCache?: (chain: unknown) => void
      }
      wallet.invalidateCommitmentsCache?.(chain)
    } catch {
      /* wallet not loaded — the SDK call below surfaces the real error */
    }

    return getWalletTransactionHistory(chain, this.walletId, startingBlock ?? null)
  }
}

/**
 * Create a RailgunWallet from mnemonic and account index.
 * Uses official @railgun-community/wallet SDK.
 */
export async function createRailgunWallet(
  mnemonic: string,
  accountIndex: number,
  passphrase?: string
): Promise<RailgunWallet> {
  const normalizedMnemonic = normalizeMnemonic(mnemonic)
  // Fold the BIP-39 passphrase into the seed the SDK actually consumes, so a
  // passphrase account derives a unique RAILGUN wallet (see deriveEffectiveRailgunMnemonic).
  const effectiveMnemonic = deriveEffectiveRailgunMnemonic(normalizedMnemonic, passphrase)

  const encryptionKey = deriveEncryptionKey(normalizedMnemonic, accountIndex, passphrase)
  const { createRailgunWallet: sdkCreateRailgunWallet } = await loadRailgunSdk()
  const sdkWallet = await sdkCreateRailgunWallet(
    encryptionKey,
    effectiveMnemonic,
    undefined // creationBlockNumberMap - optional
  )

  return new RailgunWallet({
    id: sdkWallet.id,
    railgunAddress: sdkWallet.railgunAddress,
    encryptionKey
  })
}

/**
 * Load an existing Railgun wallet by ID.
 */
export async function loadRailgunWalletById(
  walletId: string,
  encryptionKey: string
): Promise<RailgunWallet> {
  const { loadWalletByID } = await loadRailgunSdk()
  const sdkWallet = await loadWalletByID(encryptionKey, walletId, false)

  return new RailgunWallet({
    id: sdkWallet.id,
    railgunAddress: sdkWallet.railgunAddress,
    encryptionKey
  })
}

/** Minimal persisted Railgun row (subset of context `RailgunWalletData`). */
export type RailgunStoredWalletRecord = {
  accountAddr: string
  walletId?: string
  encryptionKey?: string
}

export const RAILGUN_DEFAULT_ACCOUNT_INDEX = 0

export async function loadRailgunWalletForAccountOrCreate(
  account: { addr: string },
  storedWallets: RailgunStoredWalletRecord[],
  retrieveSeedPhrase: (accountAddr: string) => Promise<{ seed: string; seedPassphrase?: string }>
): Promise<RailgunWallet> {
  const storedWallet = storedWallets.find(
    (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
  )
  if (storedWallet?.walletId && storedWallet?.encryptionKey) {
    return loadRailgunWalletById(storedWallet.walletId, storedWallet.encryptionKey)
  }
  const seedPhrase = await retrieveSeedPhrase(account.addr)
  return createRailgunWallet(
    seedPhrase.seed,
    RAILGUN_DEFAULT_ACCOUNT_INDEX,
    seedPhrase.seedPassphrase
  )
}

/** When other accounts (e.g. hardware) lack Railgun on disk — do not derive from seed here. */
export async function loadRailgunWalletForAccountFromStorageOnly(
  account: { addr: string },
  storedWallets: RailgunStoredWalletRecord[]
): Promise<RailgunWallet | null> {
  const storedWallet = storedWallets.find(
    (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
  )
  if (!storedWallet?.walletId || !storedWallet?.encryptionKey) return null
  return loadRailgunWalletById(storedWallet.walletId, storedWallet.encryptionKey)
}
